import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetPlanById, useGetPlanListByProjectId, useUpdatedPlanStatusMutation } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setPlanData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components";
import { planStatus } from "@/lib/utils";

const useEditPlan = () => {
  const {id, action, planId, planAction} = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view || planAction == routes.view;
  const isEdit = action == routes.edit || planAction == routes.edit;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const isApproveClicked = useRef<boolean>(false);

  const { projectData, planData: planDetails } = useSelector(
    (state: RootState) => state.currentData
  );
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: planList,
    isLoading: isPlanLoading,
  } = useGetPlanListByProjectId({ projectId: id as string });

  const isApprovePlanExist = useMemo(() => {
    if (planList?.data) {
      return planList?.data?.some((data: any) => data?.status == planStatus.approved.value)
    }
  }, [planList])
  
  const {data: planData} = useGetPlanById({
    planId: planId as string,
    projectId: projectData?.projectId,
    queryParams: {
      enabled: !!(planId && projectData?.projectId)
    }
  })

  useEffect(() => {
    if (planData && JSON.stringify(planData?.data) != JSON.stringify(planDetails)){
      dispatch(setPlanData(planData?.data))
    }
  }, [planData]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.projectOverview}`,
        name: `Project Overview`,
      },
      {
        link: `/${routes.admin}/${routes.projectOverview}/${id}/${action}/${routes.projectPlan}`,
        name:
          isEdit || isView
            ? `${projectData?.name} Project Details`
            : "Add Project",
      },
      {
        link: "",
        name: `${planData?.data?.name} Details`
      },
    ],
    [projectData, planData]
  );

  const { mutate: updateStatus, isPending: isUpdatePending } = useUpdatedPlanStatusMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["plan-list", id],
      });
      setShowConfirmation(false);
      setReason("");
      toast({ message: "Plan submitted for approval." });
    },
  });

  const onSubmitApproval = (isApprove: boolean) => () => {
    isApproveClicked.current = isApprove
    setShowConfirmation(true);
  }

  const onChangeReason = (e: any) => {
    setReason(e.target.value)
  }

  const onConfirmSubmit = () => {
    if (reason?.length < 21) {
      toast({type: 'error', message: 'Minimum 20 character required for reason'});
      return;
    }

    updateStatus({
      planId: planId as string,
      projectId: id as string,
      data: {
        action: isApproveClicked.current ? planStatus.approved.value : planStatus.reject.value,
        comment: reason
      }
    })
  }

  const onViewHierarchy = () => {
    navigate(`${routes.taskHierarchy}`)
  }

  const onViewTaskPlan = () => {
    navigate(`${routes.taskPlan}`)
  }

  return {
    breadcrumbData,
    showConfirmation,
    setShowConfirmation,
    onConfirmSubmit,
    onSubmitApproval,
    reason,
    onChangeReason,
    isUpdatePending,
    isPlanLoading,
    isApprovePlanExist,
    isApproveClicked,
    onViewHierarchy,
    onViewTaskPlan
  }
}

export default useEditPlan;